import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { MEDICAL_SPECIALTIES, SPECIALTY_ALL_LABEL, specialtyLabel } from "@/i18n/translations";
import { CITY_NAMES as CITIES, nearestCity } from "@/data/cities";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import Seo from "@/components/Seo";
import { MapPin, Stethoscope, Navigation, Star } from "lucide-react";
import { toast } from "sonner";
import { drName } from "@/lib/utils";

export default function Search() {
  const { t, lang } = useApp();
  const [params, setParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const city = params.get("city") || "";
  const specialty = params.get("specialty") || "";
  const specialtyOptions = [
    { value: "__all__", label: SPECIALTY_ALL_LABEL[lang] || SPECIALTY_ALL_LABEL.es },
    ...MEDICAL_SPECIALTIES.map(s => ({ value: s, label: specialtyLabel(s, lang) })),
  ];

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (city) query.city = city;
    if (specialty) query.specialty = specialty;
    api.get("/doctors", { params: query })
      .then(r => setDoctors(r.data))
      .catch(() => setDoctors([]))
      .finally(() => setLoading(false));
  }, [city, specialty]);

  const update = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    setParams(p);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalización no disponible"); return; }
    toast.info("Detectando ubicación…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { city, distance_km } = nearestCity(pos.coords.latitude, pos.coords.longitude);
        update("city", city.name);
        toast.success(`Ciudad más cercana: ${city.name} (${Math.round(distance_km)} km)`);
      },
      () => toast.error("No se pudo obtener tu ubicación"),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
      {(() => {
        const seoTitle = specialty && city ? `${specialty} en ${city}` :
                         city ? `Médicos en ${city}` :
                         specialty ? `${specialty} en Ecuador` :
                         "Buscar médicos";
        const seoDesc = specialty && city
          ? `Encuentre un especialista en ${specialty} en ${city} y reserve su cita en línea con Agendik.`
          : city
          ? `Todos los médicos verificados de ${city} disponibles en Agendik. Reserve su cita en 2 clics.`
          : specialty
          ? `Encuentre un especialista en ${specialty} en Ecuador y reserve su cita en línea con Agendik.`
          : "Encuentre un médico verificado en Ecuador y reserve su cita en línea en 2 clics.";
        const anyReal = doctors.some(d => !d.is_demo);
        return <Seo title={seoTitle} description={seoDesc} noindex={!anyReal && !loading}/>;
      })()}
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">{t.search.title}</h1>
      <Button data-testid="use-location-btn" onClick={useMyLocation}
              className="mb-4 bg-blue-600 hover:bg-blue-700 h-10">
        <Navigation size={16} className="mr-2"/> Usar mi ubicación
      </Button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 max-w-2xl">
        <Select value={city || "all"} onValueChange={(v) => update("city", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="search-city-select" className="h-11 bg-white">
            <SelectValue placeholder={t.common.city} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem data-testid="search-city-all" value="all">{t.common.all}</SelectItem>
            {CITIES.map(c => <SelectItem key={c} data-testid={`search-city-${c}`} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <SearchableSelect
          testid="search-specialty-select"
          options={specialtyOptions}
          value={specialty || "__all__"}
          onChange={(v) => update("specialty", v === "__all__" ? "" : v)}
          placeholder={t.common.specialty}
          searchPlaceholder={t.common.searchPlaceholder || "Buscar…"}
          emptyLabel={t.common.noResults || "Sin resultados"}
          className="h-11"/>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">{t.search.resultsTitle} · {doctors.length}</h2>
      </div>

      {loading ? (
        <p className="text-slate-500">{t.common.loading}</p>
      ) : doctors.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-500">{t.search.noResults}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {doctors.map(d => (
            <Card key={d.id} data-testid={`doctor-card-${d.id}`}
                  className="border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  {d.photo_url ? (
                    <img src={d.photo_url} alt={d.full_name}
                         className="w-16 h-16 rounded-2xl object-cover border border-slate-200"/>
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
                      <Stethoscope className="text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{drName(d)}</div>
                    <div className="text-sm text-blue-600 font-medium">{d.specialty}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <MapPin size={12}/> {d.city} · {d.clinic_name}
                    </div>
                    {d.rating !== null && d.rating !== undefined && (
                      <div className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <Star size={12} fill="currentColor"/> {d.rating} · {d.review_count} reseñas
                      </div>
                    )}
                  </div>
                </div>
                <Link to={`/doctor/${d.id}`}>
                  <Button data-testid={`doctor-view-${d.id}`} variant="outline"
                          className="w-full mt-4 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300">
                    {t.search.viewProfile}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
